/** @jsx jsx */
import { ReactNode, memo } from 'react';
import { jsx, css } from '@emotion/core';
import { LayoutRenderer } from 'pretty-proptypes';
import md from 'react-markings';
import { gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
  G50,
  G500,
  P50,
  P300,
  P500,
  N20,
  N30,
  N300,
  N500,
  N800,
  R500,
  B50,
} from '@atlaskit/theme/colors';

const containerStyles = css`
  border-radius: ${gridSize()}px;
  margin: ${gridSize() * 4}px -${gridSize() * 2}px 0;
  padding: ${gridSize()}px ${gridSize() * 2}px;

  :first-child {
    margin-top: 0;
  }

  :target {
    animation: highlight 250ms 2.5s ease-out forwards;
    background-color: ${token('color.background.accent.blue.subtlest', B50)};
  }

  @keyframes highlight {
    0% {
      background-color: ${token('color.background.accent.blue.subtlest', B50)};
    }
    100% {
      background-color: transparent;
    }
  }
`;

const tableStyles = css`
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: ${gridSize() / 2}px ${gridSize() * 2}px ${gridSize() / 2}px
      ${gridSize()}px;
    white-space: nowrap;
    vertical-align: top;
  }

  td {
    padding: ${gridSize() / 2}px 0 ${gridSize() / 2}px ${gridSize()}px;
    width: 100%;
  }

  tbody {
    border-bottom: none;
  }
`;

const expanderStyles = css`
  border-radius: 3px;
  border: 0;
  font-size: 14px;
  font-family: sans-serif;
  line-height: 20px;
  width: auto;
  margin: 2px 0;
  padding: 0 0.2em;
  :hover {
    cursor: pointer;
  }
`;

const typeStyles = css`
  background-color: ${token('color.background.discovery', P50)};
  border-radius: 3px;
  color: ${token('color.text.discovery', P500)};
  display: inline-block;
  margin: 2px 0;
  padding: 0 0.2em;
`;

const Type = (props: { children: ReactNode }) => (
  <span css={typeStyles} {...props} />
);

const StringType = (props: { children: ReactNode }) => (
  <span css={[typeStyles, stringType]} {...props} />
);
const Required = (props: { children: ReactNode }) => (
  <span
    css={css`
      color: ${token('color.text.danger', R500)};
    `}
  >
    {props.children}
  </span>
);

const Expander = ({
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
  children,
}: {
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    css={expanderStyles}
    style={{
      backgroundColor: isHovered
        ? token('color.background.discovery.hovered', P300)
        : token('color.background.neutral', N20),
      color: token('color.text.subtle', N300),
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </button>
);

const FunctionType = (props: { children: ReactNode }) => <span {...props} />;

const typeMeta = css`
  background-color: ${token('color.background.neutral', N20)};
  color: ${token('color.text.subtle', N300)};
`;

const TypeMeta = (props: { children: ReactNode }) => (
  <span css={[typeStyles, typeMeta]} {...props} />
);

const stringType = css`
  background-color: ${token('color.background.success', G50)};
  color: ${token('color.text.success', G500)};
`;

const PropsLayoutRenderer = memo((props: unknown) => (
  <LayoutRenderer
    requiredPropsFirst
    {...props}
    renderType={({
      name,
      required,
      deprecated,
      defaultValue,
      description,
      type,
      components,
      typeValue,
      componentDisplayName,
    }) => (
      <div
        {...(componentDisplayName && { id: `${componentDisplayName}-${name}` })}
        css={containerStyles}
      >
        <table css={tableStyles}>
          <caption
            css={css`
              text-align: left;
              margin: 0;
              font-size: 1em;
            `}
          >
            <h3
              css={css`
                border-bottom: 1px solid ${token('color.border', N30)};
                font-size: 1em;
                font-weight: normal;
                line-height: 1.4;
                margin: 0 0 ${gridSize() / 2}px 0;
                padding-bottom: ${gridSize()}px;
              `}
            >
              <code
                css={css`
                  background-color: ${token('color.background.neutral', N20)};
                  border-radius: 3px;
                  color: ${token('color.text', N800)};
                  display: inline-block;
                  font-size: 1em;
                  line-height: 20px;
                  padding: 4px 8px;
                  text-decoration: ${deprecated ? 'line-through' : 'none'};
                `}
              >
                {name}
              </code>
              {required && defaultValue === undefined && (
                <code
                  css={css`
                    margin-left: 1em;
                    color: ${token('color.text.danger', R500)};
                  `}
                >
                  required
                </code>
              )}
              {deprecated && (
                <code
                  css={css`
                    margin-left: 1em;
                    color: ${token('color.text.disabled', N500)};
                  `}
                >
                  deprecated
                </code>
              )}
            </h3>
          </caption>
          <tbody>
            <tr>
              <th scope="row">Description</th>
              <td>
                {description && (
                  <div
                    css={css`
                      p:first-of-type {
                        margin-top: 0px;
                      }
                      p:last-of-type {
                        margin-bottom: 0px;
                      }
                    `}
                  >
                    {md([
                      description && description.replace('@deprecated', ''),
                    ])}
                  </div>
                )}
              </td>
            </tr>
            {defaultValue !== undefined && (
              <tr>
                <th scope="row">Default</th>
                <td>
                  <code
                    css={css`
                      color: ${token('color.text.subtle', N300)};
                    `}
                  >
                    {defaultValue}
                  </code>
                </td>
              </tr>
            )}
            <tr>
              <th scope="row">Type</th>
              <td
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <span>
                  <code
                    css={css`
                      background: ${token('color.background.neutral', N20)};
                      color: ${token('color.text.subtle', N300)};
                      border-radius: 3px;
                      display: inline-block;
                      padding: 0 0.2em;
                    `}
                  >
                    {type}
                  </code>
                </span>
                <span>
                  <components.PropType
                    typeValue={typeValue}
                    components={{
                      ...components,
                      Type,
                      TypeMeta,
                      StringType,
                      FunctionType,
                      Required,
                      Expander,
                    }}
                  />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )}
  />
));

export default PropsLayoutRenderer;
